import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import PageTopBar from "../../../components/common/PageTopBar";
import { toolbarFieldsSx } from "../../../components/common/toolbarStyles";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  Link,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import { cellKey, useClassTimetable } from "../hooks/useClassTimetable";
import { DayOfWeek, FacultyTimetable, Period, TimetableAssignment, WORKING_DAYS } from "../types";
import { DAY_LABELS, colorOf } from "../components/timetable/timetableUi";
import { FacultyWeekGrid, WeekSummary } from "../components/timetable/FacultyWeekGrid";
import { fetchFacultyTimetable } from "../services/timetableService";
import { getFacultyList } from "../../faculty/service/facultyService";
import { FacultyListItem } from "../../faculty/types";
import { useNotify } from "../../../context/NotificationContext";
import { UnsavedChangesBar } from "../../settings/components/UnsavedChangesBar";

const headCellSx = { fontWeight: 600, py: 1, bgcolor: "grey.50" };
const stickyColSx = { position: "sticky", left: 0, zIndex: 1, bgcolor: "background.paper", borderRight: "1px solid", borderRightColor: "divider" };

type OpenCell = { anchor: HTMLElement; day: DayOfWeek; period: Period };

/**
 * Academics → Timetable: one class's week. Rows are the bell schedule's periods the grade uses,
 * columns the days it works. Click a cell to pick a subject + faculty from the class's teaching
 * assignments; a faculty already in another class then can't be picked. Changes are highlighted and
 * saved together (Save changes sends the whole week; all or nothing).
 */
function ByClassView({ viewToggle }: { viewToggle: (blocked: boolean) => React.ReactNode }) {
  const notify = useNotify();
  const { grades, gradeSectionId, pendingClassId, timetable, draft, changedCells, dirty, loadingClasses, loading, saving, error, saveError, actions } =
    useClassTimetable();
  const [openCell, setOpenCell] = useState<OpenCell | null>(null);

  const days = timetable?.days ?? {};
  const workingDays = WORKING_DAYS.filter((day) => (days[day] ?? []).length > 0);
  // only the periods the grade has on at least one day
  const periods = (timetable?.periods ?? []).filter((period) => workingDays.some((day) => (days[day] ?? []).includes(period.id!)));

  const draftAt = (day: DayOfWeek, periodId: number): number | undefined => draft[cellKey(day, periodId)];
  const assignmentOf = (id?: number) => timetable?.assignments.find((a) => a.id === id);
  const busyAt = (facultyId: number, day: DayOfWeek, periodId: number) =>
    timetable?.busy.find((b) => b.facultyId === facultyId && b.day === day && b.periodId === periodId);

  // periods per week for each assignment, as currently on screen
  const weeklyCount = useMemo(() => {
    const counts: Record<number, number> = {};
    Object.values(draft).forEach((assignmentId) => {
      counts[assignmentId] = (counts[assignmentId] ?? 0) + 1;
    });
    return counts;
  }, [draft]);

  const teachingTotal = workingDays.reduce(
    (sum, day) => sum + periods.filter((p) => !p.isBreak && (days[day] ?? []).includes(p.id!)).length,
    0
  );
  const filled = Object.keys(draft).length;

  // warn before closing/reloading the tab with unsaved changes
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const pick = (assignment: TimetableAssignment) => {
    if (!openCell) return;
    actions.assign(openCell.day, openCell.period.id!, assignment.id);
    setOpenCell(null);
  };

  const clear = () => {
    if (!openCell) return;
    actions.clear(openCell.day, openCell.period.id!);
    setOpenCell(null);
  };

  const handleSave = async () => {
    if (await actions.save()) notify("Timetable saved.", "success");
  };

  const className = (id: number | null) => {
    for (const grade of grades) {
      const section = grade.sections.find((s) => s.gradeSectionId === id);
      if (section) return `${grade.name} - ${section.name}`;
    }
    return "this class";
  };

  const classPicker = (
    <FormControl size="small" sx={{ ...toolbarFieldsSx, minWidth: 180 }} disabled={loadingClasses || grades.length === 0}>
      <InputLabel id="timetable-class">Class</InputLabel>
      <Select
        labelId="timetable-class"
        label="Class"
        value={gradeSectionId ?? ""}
        onChange={(event) => actions.selectClass(Number(event.target.value))}
        MenuProps={{ slotProps: { paper: { sx: { maxHeight: 420 } } } }}
      >
        {grades
          .filter((grade) => grade.sections.length > 0)
          .flatMap((grade) => [
            <ListSubheader key={`h-${grade.id}`} sx={{ lineHeight: "32px", fontWeight: 700, fontSize: 12, color: "text.secondary" }}>
              {grade.name}
            </ListSubheader>,
            ...grade.sections.map((section) => (
              <MenuItem key={section.gradeSectionId} value={section.gradeSectionId} sx={{ pl: 3 }}>
                {grade.name} - {section.name}
              </MenuItem>
            )),
          ])}
      </Select>
    </FormControl>
  );

  const title = (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="h6" fontWeight={600}>
        Timetable
      </Typography>
      {timetable?.academicYear && <Chip label={timetable.academicYear} size="small" variant="outlined" color="primary" sx={{ fontWeight: 600 }} />}
      {viewToggle(dirty)}
    </Stack>
  );

  const setupLink = (tab: string, label: string) => (
    <Link component={RouterLink} to={`/app/academics/setup?tab=${tab}`} fontWeight={600}>
      {label}
    </Link>
  );

  const renderCell = (day: DayOfWeek, period: Period) => {
    if (!(days[day] ?? []).includes(period.id!)) {
      return (
        <Typography variant="caption" color="text.disabled">
          —
        </Typography>
      );
    }
    // unsaved cells get an amber ring until saved or discarded
    const changed = changedCells.includes(cellKey(day, period.id!));
    const changedSx = changed ? { boxShadow: (t: any) => `0 0 0 2px ${alpha(t.palette.warning.main, 0.9)}` } : {};
    const assignment = assignmentOf(draftAt(day, period.id!));
    const open = (event: React.MouseEvent<HTMLElement>) => setOpenCell({ anchor: event.currentTarget, day, period });

    if (assignment) {
      const color = colorOf(assignment.subjectCode);
      return (
        <Box
          component="button"
          type="button"
          onClick={open}
          aria-label={`${DAY_LABELS[day]} ${period.name}: ${assignment.subjectName}, ${assignment.facultyName}`}
          sx={{
            all: "unset",
            boxSizing: "border-box",
            cursor: "pointer",
            display: "block",
            width: "100%",
            textAlign: "left",
            px: 1,
            py: 0.75,
            borderRadius: 1.5,
            bgcolor: alpha(color, 0.08),
            borderLeft: `3px solid ${color}`,
            transition: "background-color 120ms",
            "&:hover": { bgcolor: alpha(color, 0.16) },
            "&:focus-visible": { outline: `2px solid ${color}` },
            ...changedSx,
          }}
        >
          <Typography variant="body2" fontWeight={600} noWrap sx={{ color }}>
            {assignment.subjectName}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {assignment.facultyName}
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        component="button"
        type="button"
        onClick={open}
        aria-label={`${DAY_LABELS[day]} ${period.name}: empty, add`}
        sx={{
          all: "unset",
          boxSizing: "border-box",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          minHeight: 44,
          borderRadius: 1.5,
          border: "1px dashed",
          borderColor: "grey.300",
          color: "text.disabled",
          "&:hover": { borderColor: "primary.main", color: "primary.main", bgcolor: (t) => alpha(t.palette.primary.main, 0.04) },
          "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main" },
          ...changedSx,
        }}
      >
        <AddIcon fontSize="small" />
      </Box>
    );
  };

  const renderBody = () => {
    if (loadingClasses || (loading && !timetable)) {
      return (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress size={28} />
        </Box>
      );
    }
    if (error && !timetable) return <Alert severity="error">{error}</Alert>;
    if (grades.every((grade) => grade.sections.length === 0)) {
      return <Alert severity="info">No classes yet. Add grades and their sections in {setupLink("grades", "Academics → Setup → Grades")}.</Alert>;
    }
    if (!timetable) return null;
    if (!timetable.academicYear) {
      return <Alert severity="info">There is no active academic year. Activate one in {setupLink("years", "Academic Years")} to build the timetable.</Alert>;
    }
    if (timetable.periods.length === 0) {
      return <Alert severity="info">No bell schedule yet. Add the school's periods in {setupLink("bell-schedule", "Bell Schedule")}.</Alert>;
    }
    if (workingDays.length === 0) {
      return <Alert severity="info">This grade has no periods yet. Pick its periods for each day in {setupLink("grade-periods", "Grade Periods")}.</Alert>;
    }

    return (
      <>
        {timetable.assignments.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No faculty teaches this class yet. Assign subjects to faculty in{" "}
            <Link component={RouterLink} to="/app/staff?type=faculty" fontWeight={600}>
              Staff → Directory
            </Link>{" "}
            (Edit Faculty).
          </Alert>
        )}

        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, opacity: loading ? 0.6 : 1, transition: "opacity 150ms" }}>
          <Table size="small" sx={{ tableLayout: "fixed", minWidth: 140 + workingDays.length * 130 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headCellSx, ...stickyColSx, bgcolor: "grey.50", width: 120 }}>Period</TableCell>
                {workingDays.map((day) => (
                  <TableCell key={day} align="center" sx={headCellSx}>
                    {DAY_LABELS[day]}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {periods.map((period) =>
                period.isBreak ? (
                  <TableRow key={period.id}>
                    <TableCell sx={{ ...stickyColSx, bgcolor: "grey.50", py: 0.5 }}>
                      <Typography variant="caption" fontStyle="italic" color="text.secondary">
                        {period.startTime}–{period.endTime}
                      </Typography>
                    </TableCell>
                    <TableCell colSpan={workingDays.length} align="center" sx={{ bgcolor: "grey.50", py: 0.5 }}>
                      <Typography variant="caption" fontStyle="italic" color="text.secondary" letterSpacing="0.04em">
                        {period.name}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow key={period.id}>
                    <TableCell sx={{ ...stickyColSx, py: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {period.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {period.startTime}–{period.endTime}
                      </Typography>
                    </TableCell>
                    {workingDays.map((day) => (
                      <TableCell key={day} align="center" sx={{ p: 0.75 }}>
                        {renderCell(day, period)}
                      </TableCell>
                    ))}
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* summary: how full the week is, and periods per subject */}
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            Filled <b>{filled}</b> of {teachingTotal} periods
            {dirty && (
              <Typography component="span" variant="body2" color="warning.main" fontWeight={600}>
                {" "}· {changedCells.length} unsaved
              </Typography>
            )}
          </Typography>
          {timetable.assignments
            .filter((a) => weeklyCount[a.id])
            .map((a) => (
              <Chip
                key={a.id}
                size="small"
                label={`${a.subjectName} · ${weeklyCount[a.id]}/wk`}
                sx={{ bgcolor: alpha(colorOf(a.subjectCode), 0.1), color: colorOf(a.subjectCode), fontWeight: 600 }}
              />
            ))}
        </Stack>
      </>
    );
  };

  const currentId = openCell ? draftAt(openCell.day, openCell.period.id!) : undefined;

  return (
    <Box sx={{ mt: 2, mr: 2 }}>
      <PageTopBar left={title} right={classPicker} />

      {renderBody()}

      {/* subject + faculty picker for one cell */}
      <Menu
        anchorEl={openCell?.anchor}
        open={Boolean(openCell)}
        onClose={() => setOpenCell(null)}
        slotProps={{ paper: { elevation: 0, sx: { minWidth: 260, maxHeight: 420, borderRadius: 3, border: "1px solid #e5e7eb", boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)" } } }}
      >
        {openCell && (
          <Typography sx={{ px: 2, pt: 1, pb: 0.5, fontSize: 11.5, fontWeight: 700, letterSpacing: "0.04em", color: "#94a3b8", textTransform: "uppercase" }}>
            {DAY_LABELS[openCell.day]} · {openCell.period.name} · {openCell.period.startTime}–{openCell.period.endTime}
          </Typography>
        )}
        {timetable?.assignments.length === 0 && (
          <MenuItem disabled>
            <ListItemText primary="No subjects assigned to this class" />
          </MenuItem>
        )}
        {openCell &&
          timetable?.assignments.map((assignment) => {
            const busy = busyAt(assignment.facultyId, openCell.day, openCell.period.id!);
            const selected = currentId === assignment.id;
            return (
              <MenuItem key={assignment.id} onClick={() => !selected && pick(assignment)} disabled={Boolean(busy)} selected={selected} sx={{ borderRadius: 2, mx: 0.5, gap: 1 }}>
                <Box sx={{ width: 4, alignSelf: "stretch", borderRadius: 1, bgcolor: colorOf(assignment.subjectCode), my: 0.5 }} />
                <ListItemText
                  primary={assignment.subjectName}
                  secondary={busy ? `${assignment.facultyName} · busy in ${busy.className}` : assignment.facultyName}
                  slotProps={{ primary: { fontWeight: 600, fontSize: 14 }, secondary: { fontSize: 12 } }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {weeklyCount[assignment.id] ?? 0}/wk
                </Typography>
                {selected && (
                  <ListItemIcon sx={{ minWidth: 0, ml: 0.5 }}>
                    <CheckIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                )}
              </MenuItem>
            );
          })}
        {currentId !== undefined && <Divider />}
        {currentId !== undefined && (
          <MenuItem onClick={clear} sx={{ borderRadius: 2, mx: 0.5, color: "error.main" }}>
            <ListItemIcon>
              <ClearIcon fontSize="small" sx={{ color: "error.main" }} />
            </ListItemIcon>
            Clear period
          </MenuItem>
        )}
      </Menu>

      {/* switching class with unsaved changes */}
      <Dialog open={pendingClassId !== null} onClose={actions.cancelSwitch} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Discard changes to {className(gradeSectionId)}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {changedCells.length} period{changedCells.length === 1 ? "" : "s"} changed and not saved. Save them first, or discard them and open{" "}
            {className(pendingClassId)}.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={actions.cancelSwitch} sx={{ textTransform: "none" }}>
            Keep editing
          </Button>
          <Button color="error" onClick={actions.confirmSwitch} sx={{ textTransform: "none", fontWeight: 600 }}>
            Discard and switch
          </Button>
        </DialogActions>
      </Dialog>

      {/* last child of the page box, so it sticks to the bottom of the screen while scrolling */}
      <Box sx={{ height: 16 }} />
      <UnsavedChangesBar open={dirty} saving={saving} error={saveError} onDiscard={actions.discard} onSave={handleSave} />
    </Box>
  );
}

/** By Faculty: pick a faculty and see their week, read-only (editing is done By Class). */
function ByFacultyView({ viewToggle }: { viewToggle: React.ReactNode }) {
  const [faculties, setFaculties] = useState<FacultyListItem[]>([]);
  const [facultyId, setFacultyId] = useState<number | null>(null);
  const [timetable, setTimetable] = useState<FacultyTimetable | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getFacultyList()
      .then((list) => {
        const sorted = [...list].sort((a, b) => a.name.localeCompare(b.name));
        setFaculties(sorted);
        if (sorted[0]) setFacultyId(sorted[0].id);
      })
      .catch(() => setError("Failed to load faculty."))
      .finally(() => setLoadingList(false));
  }, []);

  useEffect(() => {
    if (facultyId === null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchFacultyTimetable(facultyId)
      .then((data) => !cancelled && setTimetable(data))
      .catch(() => !cancelled && setError("Failed to load the faculty's timetable."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [facultyId]);

  const title = (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="h6" fontWeight={600}>
        Timetable
      </Typography>
      {timetable?.academicYear && <Chip label={timetable.academicYear} size="small" variant="outlined" color="primary" sx={{ fontWeight: 600 }} />}
      {viewToggle}
    </Stack>
  );

  const facultyPicker = (
    <Stack direction="row" spacing={2} alignItems="center">
      {timetable && !loading && <WeekSummary timetable={timetable} />}
      <FormControl size="small" sx={{ ...toolbarFieldsSx, minWidth: 200 }} disabled={loadingList || faculties.length === 0}>
        <InputLabel id="timetable-faculty">Faculty</InputLabel>
        <Select labelId="timetable-faculty" label="Faculty" value={facultyId ?? ""} onChange={(event) => setFacultyId(Number(event.target.value))} MenuProps={{ slotProps: { paper: { sx: { maxHeight: 420 } } } }}>
          {faculties.map((faculty) => (
            <MenuItem key={faculty.id} value={faculty.id}>
              {faculty.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );

  return (
    <Box sx={{ mt: 2, mr: 2 }}>
      <PageTopBar left={title} right={facultyPicker} />
      {loadingList || (loading && !timetable) ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : faculties.length === 0 ? (
        <Alert severity="info">
          No faculty yet. Add faculty in{" "}
          <Link component={RouterLink} to="/app/staff?type=faculty" fontWeight={600}>
            Staff → Directory
          </Link>
          .
        </Alert>
      ) : (
        timetable && (
          <Box sx={{ opacity: loading ? 0.6 : 1, transition: "opacity 150ms" }}>
            <FacultyWeekGrid timetable={timetable} />
          </Box>
        )
      )}
    </Box>
  );
}

/**
 * Academics → Timetable. By Class (default) edits one class's week; By Faculty shows one faculty's
 * week read-only. The view is kept in the URL (?view=faculty).
 */
export default function TimetablePage() {
  const [params, setParams] = useSearchParams();
  const view = params.get("view") === "faculty" ? "faculty" : "class";

  const toggle = (blocked: boolean) => (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={view}
      onChange={(_, next) => next && setParams(next === "faculty" ? { view: "faculty" } : {}, { replace: true })}
      sx={{ ml: 1, "& .MuiToggleButton-root": { height: 30, px: 1.5, textTransform: "none", fontWeight: 600, fontSize: 13 } }}
    >
      <ToggleButton value="class">By Class</ToggleButton>
      <Tooltip title={blocked ? "Save or discard your changes first" : ""}>
        <span>
          <ToggleButton value="faculty" disabled={blocked} selected={view === "faculty"} onClick={() => !blocked && setParams({ view: "faculty" }, { replace: true })}>
            By Faculty
          </ToggleButton>
        </span>
      </Tooltip>
    </ToggleButtonGroup>
  );

  return view === "faculty" ? <ByFacultyView viewToggle={toggle(false)} /> : <ByClassView viewToggle={toggle} />;
}
